import React, { useState, useRef } from "react";
import { Box } from "Components/Base/Layout/Box";
import withLanguageContext from "lib/hocs/withLanguageContext";
import "./style.css";

/**
 * Dropzone component for file uploads
 * @component
 * @param {Function} props.languageContext - Function for translating text strings
 * @param {Function} props.onDrop - Callback function when files are dropped or selected
 * @param {string} props.label - Label text for the dropzone
 * @param {Array<string>} [props.allowedExtensions=[]] - List of allowed file extensions without the dot (e.g. ['jpg', 'png'])
 * @param {string} [props.className] - Additional CSS class names to apply to the dropzone
 * @param {boolean} [props.multiple=false] - Whether multiple file selection is allowed
 *
 * @example
 * // Basic usage
 * <Dropzone
 *   onDrop={files => handleFiles(files)}
 *   label="Upload your documents"
 *   allowedExtensions={['pdf', 'docx']}
 * />
 *
 * @example
 * // With multiple file support
 * <Dropzone
 *   onDrop={files => handleFiles(files)}
 *   label="Upload images"
 *   allowedExtensions={['jpg', 'png', 'gif']}
 *   multiple={true}
 *   className="custom-dropzone"
 * />
 */
const Dropzone = ({
  languageContext,
  onDrop,
  label,
  allowedExtensions = [],
  className,
  multiple = false,
  disabled = false,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef(null);
  const dropzoneId = useRef(
    `dropzone-${Math.random().toString(36).substring(2, 9)}`,
  );

  /**
   * Handles file selection from input element
   * @param {Event} event - The change event
   */
  const handleFileInputChange = (event) => {
    event.stopPropagation();

    if (!event?.target?.files?.length) {
      return;
    }

    const fileArray = Array.from(event.target.files);

    const files = fileArray.map((file) => {
      // Add preview property to the original File object instead of creating a new object
      Object.defineProperty(file, "preview", {
        value: URL.createObjectURL(file),
        writable: true,
      });
      return file;
    });

    // Call the onDrop callback with the processed files
    onDrop(files);

    // Reset the input value to ensure change events fire correctly next time
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /**
   * Processes files dropped via drag and drop
   * @param {FileList} files - The files to process
   */
  const processDroppedFiles = (files) => {
    if (!files || files.length === 0 || disabled) {
      return;
    }

    const fileArray = Array.from(files);

    const processedFiles = fileArray.map((file) => {
      // Add preview property to the original File object instead of creating a new object
      Object.defineProperty(file, "preview", {
        value: URL.createObjectURL(file),
        writable: true,
      });
      return file;
    });

    onDrop(processedFiles);
  };

  /**
   * Opens the file dialog explicitly when the dropzone is clicked
   */
  const handleDropzoneClick = () => {
    // Trigger file input click directly
    if (inputRef.current) {
      // Use a direct method call not tied to React's event system
      inputRef.current.click();
    }
  };

  /**
   * Handle keyboard events for accessibility
   * @param {KeyboardEvent} event - The keyboard event
   */
  const handleKeyDown = (event) => {
    // Activate on Enter or Space
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleDropzoneClick();
    }
  };

  /**
   * Prevents default behavior on drag over
   * @param {DragEvent} event - The drag event
   */
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Handles drag enter event and updates UI state
   * @param {DragEvent} event - The drag event
   */
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  /**
   * Handles drag leave event and updates UI state
   * @param {DragEvent} event - The drag event
   */
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  /**
   * Handles file drop event
   * @param {DragEvent} event - The drop event
   */
  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      processDroppedFiles(event.dataTransfer.files);
    }
  };

  // Generate accessible instructions
  const getInstructions = () => {
    const dragText = languageContext("fileuploadinfo");
    return `${dragText}.`;
  };

  return (
    <div>
      {/* Separate file input not connected to any label */}
      <input
        type="file"
        ref={inputRef}
        disabled={disabled}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
        accept={allowedExtensions?.map((ext) => `.${ext}`).join(",")}
        multiple={multiple}
        aria-hidden="true"
      />

      {/* Dropzone box with click handler to open file dialog */}
      <Box
        className={`dropzone ${isDragActive ? "dropzone-active" : ""} ${className || ""}`}
        onClick={handleDropzoneClick}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleFileDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex="0"
        aria-label={`${label} - ${languageContext("fileupload")}`}
        id={dropzoneId.current}
        aria-describedby={`${dropzoneId.current}-instructions`}
        disabled={disabled}
      >
        <div className="dropzone-label">{label}</div>
        <span id={`${dropzoneId.current}-instructions`} className="sr-only">
          {getInstructions()}
        </span>
      </Box>
    </div>
  );
};

export default withLanguageContext(Dropzone, "Common");
